// Transport-independent recorder. A stop owns cumulative foreground time, so
// retries and out-of-order delivery cannot add the same seconds twice.
export function createVisitRecorder({ send, identity, now = Date.now, id, isVisible = () => true }) {
  const pending = new Map();
  let current = null, tick = now(), visible = isVisible(), sending = false;
  function accrue() {
    const time = now();
    if (current && visible) {
      // A suspended device must not turn an overnight pause into a long visit.
      current.visible_ms += Math.max(0, Math.min(60000, time - tick));
      current.revision += 1;
      pending.set(current.id, current);
    }
    tick = time;
  }
  function setContext(context) {
    accrue();
    const key = context ? JSON.stringify(context) : '';
    if (current?.key === key) return;
    current = context && visible ? { ...context, id: id(), started_at: now(), visible_ms: 0, events: [], revision: 0, key } : null;
    if (current) pending.set(current.id, current);
    // Bound memory during long network outages. Browsing must remain usable.
    while (pending.size > 200) pending.delete(pending.keys().next().value);
  }
  function action(action, target = '', detail = '') {
    accrue();
    if (!current || !visible) return;
    current.events.push({ id: id(), at: now(), action, target, detail });
    if (current.events.length > 200) current.events.shift();
    current.revision += 1;
  }
  async function flush(beacon = false) {
    accrue();
    if (sending && !beacon) return;
    if (!pending.size) return;
    let allowance = 20;
    const rows = [...pending.values()].slice(0, 10).map(row => {
      const events = row.events.slice(0, allowance); allowance -= events.length;
      const { revision, ...visit } = row;
      delete visit.key;
      return { row, revision, visit: { ...visit, events } };
    });
    sending = true;
    try {
      const ok = await send({ context: identity(), visits: rows.map(row => row.visit) }, beacon);
      if (!ok) return;
      for (const { row, revision, visit } of rows) {
        const sent = new Set(visit.events.map(event => event.id));
        row.events = row.events.filter(event => !sent.has(event.id));
        if (row.revision === revision && !row.events.length) pending.delete(row.id);
      }
    } catch { /* A telemetry outage never interrupts the visit. */ }
    finally { sending = false; }
  }
  return {
    setContext, action, flush,
    visibility(value) { accrue(); visible = value; },
    discard() { current = null; pending.clear(); tick = now(); },
  };
}
